import {
    defaultShouldDehydrateQuery,
    dehydrate,
    type DehydratedState,
    hydrate,
    type InfiniteData,
    type Query,
    QueryClient,
    QueryClientConfig,
} from '@tanstack/react-query';
import { IncomingMessage } from 'http';

import { regions } from '@/config/locales';
import { pageToIDMapping, PageType } from '@/config/nav/pageMapping';
import { navParams } from '@/config/nav/paramConfig';
import type { UserFullProfileQuery } from '@/graphql/__generated/gql/graphql';
import { prefetchGetNav } from '@/hooks/nav/useGetNavById';
import {
    buildFullUserProfile,
    cacheKey as fullUserCacheKey,
    prefetchFullUser,
} from '@/hooks/providers/useFullUser';
import { appLogger } from '@/utils/app-logger/appLogger';
import { getParamsFromObject } from '@/utils/getParamsFromObject';
import { getRegion, PTV_REGION_URL_SEGMENTS } from '@/utils/server/region-utils';

import { INTL_SEGMENT } from '../regionSegments';
import {
    type InfinitePrefetchDefinition,
    isDeterministicClientError,
    type PrefetchQueryDefinition,
    type StandardPrefetchDefinition,
    toPrefetchQueryDefinition,
} from '../request';

/** SSR prefetch/fetch factory. Return type is `unknown` at the boundary so literal factories keep precise inference in `fetchSSR`. */
export type PrefetchFunction = (req: IncomingMessage) => unknown;

const fetchQueryDefinition = (
    queryClient: QueryClient,
    definition: PrefetchQueryDefinition,
): Promise<unknown> => {
    if (isInfinitePrefetchDefinition(definition)) {
        return queryClient.fetchInfiniteQuery(definition);
    }
    return queryClient.fetchQuery(definition);
};

const prefetchQueryDefinition = (
    queryClient: QueryClient,
    definition: PrefetchQueryDefinition,
): Promise<unknown> => {
    if (isInfinitePrefetchDefinition(definition)) {
        return queryClient.prefetchInfiniteQuery(definition);
    }
    return queryClient.prefetchQuery(definition);
};

const prefetchDefinitions = (
    queryClient: QueryClient,
    definitions: readonly PrefetchQueryDefinition[],
): Promise<unknown[]> =>
    Promise.all(definitions.map((definition) => prefetchQueryDefinition(queryClient, definition)));

const fetchDefinitions = (
    queryClient: QueryClient,
    definitions: readonly PrefetchQueryDefinition[],
): Promise<unknown[]> =>
    Promise.all(definitions.map((definition) => fetchQueryDefinition(queryClient, definition)));

const isInfinitePrefetchDefinition = (
    def: PrefetchQueryDefinition,
): def is InfinitePrefetchDefinition => 'initialPageParam' in def && 'getNextPageParam' in def;

const prefetchSSR = async <const T extends readonly PrefetchFunction[]>(
    prefetchArray: T,
    queryClient: QueryClient,
    req: IncomingMessage,
): Promise<void> => {
    await Promise.all(
        prefetchArray.map((fn) => {
            const queryDefinition = toPrefetchQueryDefinition(fn(req));
            if (Array.isArray(queryDefinition)) {
                return prefetchDefinitions(queryClient, queryDefinition);
            }
            return prefetchQueryDefinition(queryClient, queryDefinition);
        }),
    );
};

const getPathnameFromRequestUrl = (pathOrUrl: string): string => {
    const withoutQueryOrHash = pathOrUrl.split(/[?#]/, 1)[0] ?? pathOrUrl;

    if (withoutQueryOrHash.startsWith('http://') || withoutQueryOrHash.startsWith('https://')) {
        try {
            return new URL(withoutQueryOrHash).pathname;
        } catch {
            return withoutQueryOrHash;
        }
    }

    return withoutQueryOrHash.startsWith('/') ? withoutQueryOrHash : `/${withoutQueryOrHash}`;
};

// One optional leading region segment. The middleware and the regional rewrites strip a region
// prefix before the page renders, but `req.url` still carries it, so the gate must accept every
// segment they accept: P+ and PTV country codes plus PTV's marketing regions (`latam`, `gsa`,
// `gb_ie`) and the `intl` rewrite. Anything wider also matches dynamic pages such as `/shows/api/`.
// Built on first use rather than at import: many suites replace `region-utils` with a factory that
// only defines `getRegion`, and this module must stay importable under such a mock.
const escapeForRegExp = (segment: string) => segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const buildRegionPrefix = (): string => {
    const segments = new Set<string>([
        ...(regions?.pplus ?? []),
        ...(PTV_REGION_URL_SEGMENTS ?? []),
        INTL_SEGMENT,
    ]);
    return `(?:(?:${[...segments].map(escapeForRegExp).join('|')})\\/)?`;
};

/**
 * Matched against the request pathname (origin-form or absolute-form URL, query and hash stripped)
 * and anchored, because a substring match here silently turns a content page into a no-prefetch
 * render (`/shows/the-republic-of-sarah/` once matched `public`).
 */
const buildCsrUrlPatterns = (regionPrefix: string): RegExp[] => [
    new RegExp(`^\\/${regionPrefix}public\\/`),
    /** Next.js client navigations load getServerSideProps via `/_next/data/...`; those requests must run SSR prefetch. */
    /^\/_next\/(?!data\/)/,
    /\.map$/,
    new RegExp(`^\\/${regionPrefix}manifest\\.json$`),
    /**
     * API request URLs: `/api/`, the `/tn/api/` rewrite, and the optional region prefix. An API request
     * never renders a page, and when its route fails to load Next renders `/_error` for it, where SSR
     * prefetches would loop back into this server; see
     * .ai-rules/03-frameworks/nextjs/graphql-api-routes.md.
     */
    new RegExp(`^\\/${regionPrefix}(?:tn\\/)?api\\/`),
    /^\/sw\.js$/,
    /^\/\.well-known\/appspecific\/com\.chrome\.devtools\.json$/,
];

let csrUrlPatterns: RegExp[] | undefined;
const getCsrUrlPatterns = (): RegExp[] =>
    (csrUrlPatterns ??= buildCsrUrlPatterns(buildRegionPrefix()));

export const checkIsCSR = (req?: IncomingMessage): req is undefined => {
    if (!req?.url) {
        return !req;
    }
    const pathname = getPathnameFromRequestUrl(req.url);
    return getCsrUrlPatterns().some((pattern) => pattern.test(pathname));
};

/**
 * Check if the request is for a static asset that should be skipped
 */
export function isStaticAsset(pathOrUrl: string): boolean {
    const pathname = getPathnameFromRequestUrl(pathOrUrl);

    // Skip common static file extensions
    const staticExtensions = [
        '.js',
        '.css',
        '.png',
        '.jpg',
        '.jpeg',
        '.gif',
        '.svg',
        '.ico',
        '.woff',
        '.woff2',
        '.ttf',
        '.eot',
        '.otf',
        '.webp',
        '.avif',
        '.json',
        '.xml',
        '.txt',
        '.map',
    ];

    const hasStaticExtension = staticExtensions.some((ext) => pathname.toLowerCase().endsWith(ext));

    // Skip common static file paths
    const staticPaths = ['/favicon.ico', '/robots.txt', '/sitemap.xml', '/manifest.json'];
    const isFaviconPath =
        pathname === '/favicon' || pathname === '/favicon/' || pathname.startsWith('/favicon/');
    // Well-known URIs (RFC 8615) are machine-read files and often have no extension
    const isWellKnownPath = pathname.startsWith('/.well-known/');

    return hasStaticExtension || staticPaths.includes(pathname) || isFaviconPath || isWellKnownPath;
}

export function is500Page(pathname: string): boolean {
    return pathname === '/500';
}

export function is404Page(pathname: string): boolean {
    return pathname === '/404';
}

export function isStaticPage(pathname: string): boolean {
    return is500Page(pathname) || is404Page(pathname);
}

export const getDataSSR = async (
    fetchFn: PrefetchFunction[],
    req?: IncomingMessage,
    queryClientInstance?: QueryClient,
) => {
    const isCSR = !req || checkIsCSR(req);

    if (isCSR) {
        return null;
    }

    const queryClient = queryClientInstance || getQueryClient();

    return await fetchSSR(fetchFn, queryClient, req);
};

/**
 * SSR QueryClient options: retries once on transient errors but not deterministic 4xx (see `isDeterministicClientError`).
 * Usage: `ssrState(req, { defaultOptions: { queries: SSR_RETRY_ONCE_QUERY_OPTIONS } })`.
 */
export const SSR_RETRY_ONCE_QUERY_OPTIONS = {
    retry: (failureCount: number, error: unknown): boolean =>
        failureCount < 1 && !isDeterministicClientError(error),
    retryDelay: 250,
} as const;

/** Builds the SSR QueryClient; caller `defaultOptions.queries` are merged over the SSR defaults. */
export const getQueryClient = (queryClientConfig?: QueryClientConfig) => {
    return new QueryClient({
        ...queryClientConfig,
        defaultOptions: {
            ...queryClientConfig?.defaultOptions,
            queries: {
                staleTime: 60 * 1000,
                ...queryClientConfig?.defaultOptions?.queries,
            },
        },
    });
};

export const getDehydratedState = async (
    prefetchArray: PrefetchFunction[],
    req?: IncomingMessage,
) => {
    if (!req || checkIsCSR(req)) {
        return null;
    }

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 60 * 1000,
            },
        },
    });
    await prefetchSSR(prefetchArray, queryClient, req);
    const dehydratedState = dehydrate(queryClient);
    if (!dehydratedState) {
        return null;
    }

    return dehydratedState;
};

/** Extracts fetch/prefetch result data from a typed standard or infinite definition. */
type ExtractQueryDataType<T> =
    T extends InfinitePrefetchDefinition<infer U, infer P>
        ? InfiniteData<U | null, P>
        : T extends StandardPrefetchDefinition<infer U>
          ? U | null
          : never;

/** Infers resolved data from a prefetch factory's return type. */
type ExtractPrefetchDataType<T> = T extends (req: IncomingMessage) => infer R
    ? R extends readonly (infer QueryDef)[]
        ? ExtractQueryDataType<QueryDef>[]
        : ExtractQueryDataType<R>
    : never;

/** Maps an array of PrefetchFunctions to a tuple of their resolved data types. */
type InferFetchSSRReturnType<T extends readonly PrefetchFunction[]> = {
    readonly [K in keyof T]: ExtractPrefetchDataType<T[K]>;
};

// TODO: this resolves the array via Promise.all with no per-item try/catch, so one definition
// rejecting discards every result in the call, even ones that already succeeded. Callers that
// need one definition's failure to not wipe out another's result currently have to make separate
// fetchSSR calls instead (see apps/signup/src/pages/index.tsx). Either support scoped/partial
// failures here (e.g. resolve via Promise.allSettled and let callers inspect per-item status), or
// stop accepting arrays and make callers needing independent error handling call fetchSSR per item.
const fetchSSR = async <const T extends readonly PrefetchFunction[]>(
    prefetchArray: T,
    queryClient: QueryClient,
    req: IncomingMessage,
): Promise<InferFetchSSRReturnType<T>> => {
    const results = await Promise.all(
        prefetchArray.map(async (fn) => {
            const queryDefinition = toPrefetchQueryDefinition(fn(req));
            if (Array.isArray(queryDefinition)) {
                return fetchDefinitions(queryClient, queryDefinition);
            } else {
                return fetchQueryDefinition(queryClient, queryDefinition);
            }
        }),
    );
    return results as InferFetchSSRReturnType<T>;
};

type FetchSSRReturnType<T extends readonly PrefetchFunction[]> = InferFetchSSRReturnType<T>;

type CarouselQueryKeyParams = {
    carouselParams?: { carouselId?: string | null; model?: string | null };
};

/** Best-effort row attribution for a dropped carousel query; the token is never included. */
const describeDroppedQuery = (queryKey: readonly unknown[]) => {
    const params = queryKey[1];
    if (typeof params !== 'object' || params === null || !('carouselParams' in params)) {
        return { cacheKey: String(queryKey[0]) };
    }
    const { carouselParams } = params as CarouselQueryKeyParams;
    return {
        cacheKey: String(queryKey[0]),
        carouselId: carouselParams?.carouselId ?? undefined,
        model: carouselParams?.model ?? undefined,
    };
};

/**
 * Dehydration serializes successful queries only, so a prefetch that ended in `error` disappears
 * from `pageProps` and a failed rail becomes indistinguishable from one that was never selected.
 * The verdict here is the default one — the payload is unchanged — but the drop stops being silent.
 *
 * Only `error` is logged. The default predicate also drops `pending` queries, which on timeout-
 * capped pages is every prefetch that missed its deadline — a designed fallback, not a fault, and
 * the dominant case by volume. Warning on those would bury the failures this exists to surface.
 */
const shouldDehydrateQueryLoggingDrops = (query: Query): boolean => {
    const shouldDehydrate = defaultShouldDehydrateQuery(query);
    if (shouldDehydrate || query.state.status !== 'error') {
        return shouldDehydrate;
    }
    appLogger.warn('Dropping failed query from dehydrated state', {
        component: 'prefetch-utils',
        ...describeDroppedQuery(query.queryKey),
        status: query.state.status,
        fetchStatus: query.state.fetchStatus,
        errorName: query.state.error instanceof Error ? query.state.error.name : undefined,
    });
    return shouldDehydrate;
};

/**
 * Returns an object with SSR utilities for React Query, including prefetching, fetching, and dehydrating state.
 *
 * @param req - The incoming HTTP request (undefined for CSR).
 * @param queryClientConfig - Optional configuration for QueryClient, or an existing QueryClient instance.
 * @returns An object with SSR utility methods:
 *   - prefetchSSR: Triggers React Query's prefetching for SSR, populating the cache for later hydration. Use when you want to prepare queries for hydration but do not need immediate results. Returns undefined in CSR.
 *   - fetchSSR: Executes React Query's fetch for SSR, returning resolved data for each query. Use when you need the actual data immediately for SSR rendering. Returns a resolved empty array in CSR.
 *   - dehydrate: Dehydrates the QueryClient state for serialization and transfer to the client.
 *
 * Usage:
 *   - Use prefetchSSR to warm up the cache before dehydrating state for React Query hydration on the client.
 *   - Use fetchSSR if you need to access the resolved data directly during SSR (e.g., for rendering or logic).
 */
export const ssrState = (
    req?: IncomingMessage,
    queryClientConfig?: QueryClientConfig & { queryClient?: QueryClient },
) => {
    let queryClient: QueryClient;
    if (queryClientConfig?.queryClient) {
        queryClient = queryClientConfig.queryClient;
    } else {
        queryClient = getQueryClient(queryClientConfig);
    }

    const isCSR = !req || checkIsCSR(req);

    return {
        prefetchSSR: <const T extends readonly PrefetchFunction[]>(prefetchArray: T) =>
            isCSR ? undefined : prefetchSSR(prefetchArray, queryClient, req),
        fetchSSR: <const T extends readonly PrefetchFunction[]>(prefetchArray: T) =>
            isCSR ? ([] as FetchSSRReturnType<T>) : fetchSSR(prefetchArray, queryClient, req),
        dehydrate: () => {
            const dehydratedState = dehydrate(queryClient, {
                shouldDehydrateQuery: shouldDehydrateQueryLoggingDrops,
            });
            return !dehydratedState ? null : dehydratedState;
        },
        queryClient: queryClient,
    };
};

const dehydratedQueryDedupeKey = (q: DehydratedState['queries'][number]): string =>
    typeof q.queryHash === 'string' && q.queryHash.length > 0
        ? q.queryHash
        : JSON.stringify(q.queryKey);

/**
 * Merges multiple TanStack Query dehydrated payloads (e.g. remote MFE props + app-shell nav).
 * When the same query appears more than once, the last occurrence wins.
 */
export function mergeDehydratedStates(
    ...states: Array<DehydratedState | null | undefined>
): DehydratedState {
    const byKey = new Map<string, DehydratedState['queries'][number]>();
    for (const state of states) {
        if (!state?.queries?.length) {
            continue;
        }
        for (const q of state.queries) {
            byKey.set(dehydratedQueryDedupeKey(q), q);
        }
    }
    const mutations: DehydratedState['mutations'] = [];
    for (const state of states) {
        if (state?.mutations?.length) {
            mutations.push(...state.mutations);
        }
    }
    return {
        queries: [...byKey.values()],
        mutations,
    };
}

/**
 * Cache keys whose dehydrated queries carry personalized (PII) data and must never be serialized
 * into a shared/cacheable page payload — currently just the full user profile. The `nav-by-id`
 * query is deliberately excluded: on cacheable routes {@link dehydrateTopNavQueries} already
 * prefetches it with the anonymous profile, so the dehydrated nav is generic and worth keeping for
 * server-rendered (no-JS) navigation.
 */
export const USER_DATA_CACHE_KEYS: readonly string[] = [fullUserCacheKey];

/** Resolves the cache key (first queryKey segment) for a dehydrated query, falling back to its hash. */
const dehydratedQueryCacheKey = (q: DehydratedState['queries'][number]): string => {
    const firstKeySegment = Array.isArray(q.queryKey) ? q.queryKey[0] : undefined;
    if (typeof firstKeySegment === 'string') {
        return firstKeySegment;
    }
    if (typeof q.queryHash !== 'string') {
        return '';
    }
    try {
        const parsed = JSON.parse(q.queryHash);
        return Array.isArray(parsed) && typeof parsed[0] === 'string' ? parsed[0] : q.queryHash;
    } catch {
        return q.queryHash;
    }
};

/**
 * Removes personalized user-data queries from a dehydrated React Query state before it is serialized
 * into a page payload. Intended for `getServerSideProps` on cacheable (shared-across-users) pages so
 * PII (e.g. the full user profile) never pollutes `__NEXT_DATA__` / `/_next/data/*.json`.
 *
 * Pure: returns a new {@link DehydratedState} and never mutates the input. A query is stripped when
 * its cache key (first `queryKey` segment, or the first element of the parsed `queryHash`) is in
 * `cacheKeys`. Mutations pass through untouched.
 *
 * @param state - The dehydrated state to filter (nullish yields an empty state).
 * @param cacheKeys - Cache keys to strip; defaults to {@link USER_DATA_CACHE_KEYS}.
 */
export function stripUserDataFromDehydratedState(
    state: DehydratedState | null | undefined,
    cacheKeys: readonly string[] = USER_DATA_CACHE_KEYS,
): DehydratedState {
    if (!state) {
        return emptyDehydratedState();
    }
    const denylist = new Set(cacheKeys);
    return {
        queries: (state.queries ?? []).filter((q) => !denylist.has(dehydratedQueryCacheKey(q))),
        mutations: state.mutations ?? [],
    };
}

/**
 * Prefetches the same GraphQL nav query used by {@link useGetNav} / {@link TopNav}, so primary
 * nav links render on the server (e.g. when JS is disabled).
 *
 * `extraParams` must match the key `useGetNav` computes on the client, or the prefetched entry is
 * an orphan that the client refetches anyway:
 *  - Personalized routes (default): fetch the authenticated profile and derive params from it.
 *  - Cacheable routes (`isCacheable`): do NOT fetch the profile — that would serialize PII into the
 *    shared payload. Derive params from the anonymous profile instead, which is exactly what
 *    `useFullUser` falls back to (`ANON_PROFILE`) during SSR once the profile is stripped, so the
 *    dehydrated nav is a cache hit and renders server-side for everyone.
 *
 * When `preHydratedState` is provided (e.g. from the remote MFE's dehydrated props), the
 * QueryClient is seeded from that state first so matching prefetches become instant cache hits.
 */
export async function dehydrateTopNavQueries(
    req: IncomingMessage | undefined,
    pageID?: string | number | null,
    preHydratedState?: DehydratedState | null,
    isCacheable = false,
): Promise<DehydratedState | null> {
    if (!req || checkIsCSR(req)) {
        return null;
    }
    const queryClient = getQueryClient();
    if (preHydratedState) {
        // Strip mutations before hydrating: mutations are client-only and re-including them
        // here would duplicate them when remoteDehydrated is merged again via
        // mergeDehydratedStates. In SSR contexts there are no mutations to preserve anyway.
        hydrate(queryClient, { ...preHydratedState, mutations: [] });
    }
    const state = ssrState(req, { queryClient });
    // `buildFullUserProfile(undefined, region)` yields the same anonymous fallback that `useFullUser`
    // resolves to when no profile is cached — keeping the prefetched nav key aligned with SSR render.
    // Passing the request-resolved region matches the client's `useRegion`-derived country, so the
    // anonymous nav key doesn't orphan (and refetch) for intl sessions.
    const anonymousRegion = getRegion(req);
    let userProfile = buildFullUserProfile(undefined, anonymousRegion);
    if (!isCacheable) {
        const [userResp] = await state.fetchSSR([prefetchFullUser]);
        userProfile = buildFullUserProfile(userResp as UserFullProfileQuery, anonymousRegion);
    }
    const userParams = getParamsFromObject(userProfile.userFullProfile, navParams);
    await state.fetchSSR([
        (incomingReq) => prefetchGetNav(incomingReq, pageID ?? undefined, userParams),
    ]);
    return state.dehydrate();
}

const emptyDehydratedState = (): DehydratedState => ({
    queries: [],
    mutations: [],
});

/**
 * Prefetches top-nav GraphQL data and merges it into existing dehydrated React Query state (remote
 * MFE props and/or {@link getDehydratedState}). Use this for app-shell pages that compose TopNav SSR.
 *
 * Optionally accepts an `extraPrefetch` list (e.g. footer links) whose dehydrated state is fetched
 * in parallel with the top-nav queries and merged into the final result. Pass it instead of doing
 * a separate `getDehydratedState` call so both round-trips happen concurrently.
 */
export async function mergeDehydratedStateWithTopNav(
    req: IncomingMessage | undefined,
    remoteDehydrated: DehydratedState | null | undefined,
    pageID?: string | number | null,
    extraPrefetch?: PrefetchFunction[],
    isCacheable = false,
): Promise<DehydratedState> {
    const [topNavDehydrated, extraDehydrated] = await Promise.all([
        // Pass remoteDehydrated to seed the QueryClient so prefetchFullUser and prefetchGetNav
        // resolve as cache hits when hubs GSSP has already fetched them with identical cache keys.
        // On cacheable routes the nav is prefetched anonymously (no profile fetch).
        dehydrateTopNavQueries(req, pageID, remoteDehydrated, isCacheable),
        extraPrefetch?.length ? getDehydratedState(extraPrefetch, req) : Promise.resolve(null),
    ]);
    return mergeDehydratedStates(
        remoteDehydrated ?? emptyDehydratedState(),
        topNavDehydrated,
        extraDehydrated,
    );
}

/** Default nav page id for hub browse pages (matches {@link PageType.HUBS} in page mapping). */
export const topNavPageIdHubs = pageToIDMapping[PageType.HUBS];
